import type { InferInsertModel } from 'drizzle-orm'
import { eq } from 'drizzle-orm'
import {
  accountsTable,
  eventPeopleTable,
  eventsTable,
  eventTagsTable,
  locationsTable,
  peopleTable,
  personAccountLinksTable,
  personRelationshipsTable,
  scopesTable,
  scopeMembersTable,
  tagsTable,
} from '../../client-db/src/db/schema'

type DrizzleDb = any

type DemoEvent = {
  key: string
  title: string
  description: string
  text: string
  date: Date
  endDate?: Date
  parentKey?: string
  locationKey: string
  tags: string[]
  people: string[]
  importance?: number
}

function normalizeName(value: string) {
  return value.trim().toLowerCase()
}

async function hasAnyEvents(db: DrizzleDb) {
  const existingEvents = await db.select({ id: eventsTable.id }).from(eventsTable).limit(1)
  return existingEvents.length > 0
}

export async function seedDemoData(db: DrizzleDb): Promise<void> {
  if (await hasAnyEvents(db)) {
    return
  }

  const [scope] = await db
    .insert(scopesTable)
    .values({
      type: 'HOUSEHOLD',
      name: 'Carter Family',
    })
    .returning({ id: scopesTable.id })

  if (!scope?.id) {
    throw new Error('Failed to create demo scope')
  }

  const [emilyAccount] = await db
    .insert(accountsTable)
    .values({
      email: 'emily.carter@example.com',
      givenName: 'Emily',
      familyName: 'Carter',
      displayName: 'Emily Carter',
      avatarUrl: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=640&q=80',
    })
    .returning({ id: accountsTable.id })

  const [danielAccount] = await db
    .insert(accountsTable)
    .values({
      email: 'daniel.carter@example.com',
      givenName: 'Daniel',
      familyName: 'Carter',
      displayName: 'Daniel Carter',
      avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=640&q=80',
    })
    .returning({ id: accountsTable.id })

  if (!danielAccount?.id || !emilyAccount?.id) {
    throw new Error('Failed to create demo accounts')
  }

  await db.insert(scopeMembersTable).values([
    { scopeId: scope.id, accountId: emilyAccount.id, role: 'OWNER' },
    { scopeId: scope.id, accountId: danielAccount.id, role: 'ADULT_MEMBER' },
  ])

  const demoPeople = [
    {
      key: 'daniel',
      firstName: 'Daniel',
      lastName: 'Carter',
      fullName: 'Daniel Carter',
      notes: 'Dad. Loves trail running, grilling, and National Parks road trips.',
    },
    {
      key: 'emily',
      firstName: 'Emily',
      lastName: 'Carter',
      fullName: 'Emily Carter',
      notes: 'Mom. Elementary school teacher and family travel planner.',
    },
    {
      key: 'olivia',
      firstName: 'Olivia',
      lastName: 'Carter',
      fullName: 'Olivia Carter',
      notes: 'Oldest daughter. Plays soccer and sings in choir.',
    },
    {
      key: 'noah',
      firstName: 'Noah',
      lastName: 'Carter',
      fullName: 'Noah Carter',
      notes: 'Older son. Baseball fan and backyard inventor.',
    },
    {
      key: 'ava',
      firstName: 'Ava',
      lastName: 'Carter',
      fullName: 'Ava Carter',
      notes: 'Younger daughter. Loves dance and drawing animals.',
    },
    {
      key: 'ethan',
      firstName: 'Ethan',
      lastName: 'Carter',
      fullName: 'Ethan Carter',
      notes: 'Youngest son. Legos, dinosaurs, and pancakes.',
    },
    {
      key: 'robert',
      firstName: 'Robert',
      lastName: 'Carter',
      fullName: 'Robert Carter',
      notes: 'Grandpa. Retired engineer who always brings classic road-trip playlists.',
    },
    {
      key: 'linda',
      firstName: 'Linda',
      lastName: 'Carter',
      fullName: 'Linda Carter',
      notes: 'Grandma. Bakes cinnamon rolls and never misses a school milestone.',
    },
  ] as const

  const personIdByKey = new Map<string, number>()
  for (const person of demoPeople) {
    const [insertedPerson] = await db
      .insert(peopleTable)
      .values({
        scopeId: scope.id,
        firstName: person.firstName,
        lastName: person.lastName,
        fullName: person.fullName,
        notes: person.notes,
      })
      .returning({ id: peopleTable.id })
    if (!insertedPerson?.id) {
      throw new Error(`Failed to create demo person: ${person.fullName}`)
    }
    personIdByKey.set(person.key, insertedPerson.id)
  }

  const danielPersonId = personIdByKey.get('daniel')
  const emilyPersonId = personIdByKey.get('emily')
  const oliviaPersonId = personIdByKey.get('olivia')
  const noahPersonId = personIdByKey.get('noah')
  const avaPersonId = personIdByKey.get('ava')
  const ethanPersonId = personIdByKey.get('ethan')
  const robertPersonId = personIdByKey.get('robert')
  const lindaPersonId = personIdByKey.get('linda')

  if (
    !danielPersonId ||
    !emilyPersonId ||
    !oliviaPersonId ||
    !noahPersonId ||
    !avaPersonId ||
    !ethanPersonId ||
    !robertPersonId ||
    !lindaPersonId
  ) {
    throw new Error('Failed to resolve demo person IDs')
  }

  await db.insert(personAccountLinksTable).values([
    {
      personId: emilyPersonId,
      accountId: emilyAccount.id,
      status: 'SELF',
      source: 'LOCAL',
      isPrimary: true,
    },
    {
      personId: danielPersonId,
      accountId: danielAccount.id,
      status: 'VERIFIED',
      source: 'LOCAL',
      isPrimary: true,
    },
  ])

  await db.insert(personRelationshipsTable).values([
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: emilyPersonId,
      relationshipType: 'SPOUSE',
      relationshipLabel: 'wife',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: emilyPersonId,
      targetPersonId: danielPersonId,
      relationshipType: 'SPOUSE',
      relationshipLabel: 'husband',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: oliviaPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: noahPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: avaPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: ethanPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: emilyPersonId,
      targetPersonId: oliviaPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: emilyPersonId,
      targetPersonId: noahPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: emilyPersonId,
      targetPersonId: avaPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: emilyPersonId,
      targetPersonId: ethanPersonId,
      relationshipType: 'CHILD',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: robertPersonId,
      relationshipType: 'PARENT',
      relationshipLabel: 'father',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: danielPersonId,
      targetPersonId: lindaPersonId,
      relationshipType: 'PARENT',
      relationshipLabel: 'mother',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: robertPersonId,
      targetPersonId: danielPersonId,
      relationshipType: 'CHILD',
      relationshipLabel: 'son',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: lindaPersonId,
      targetPersonId: danielPersonId,
      relationshipType: 'CHILD',
      relationshipLabel: 'son',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: robertPersonId,
      targetPersonId: lindaPersonId,
      relationshipType: 'SPOUSE',
      relationshipLabel: 'wife',
    },
    {
      ownerAccountId: emilyAccount.id,
      subjectPersonId: lindaPersonId,
      targetPersonId: robertPersonId,
      relationshipType: 'SPOUSE',
      relationshipLabel: 'husband',
    },
  ])

  const demoTags = [
    { name: 'Milestone', color: '#DC2626' },
    { name: 'Birthday', color: '#EA580C' },
    { name: 'Vacation', color: '#0284C7' },
    { name: 'Family', color: '#7C3AED' },
    { name: 'School', color: '#0F766E' },
    { name: 'Sports', color: '#15803D' },
    { name: 'Career', color: '#334155' },
    { name: 'Holiday', color: '#B45309' },
    { name: 'Travel', color: '#1D4ED8' },
    { name: 'Achievement', color: '#7E22CE' },
  ] as const

  const tagIdByNormalizedName = new Map<string, number>()
  for (const tag of demoTags) {
    const [insertedTag] = await db
      .insert(tagsTable)
      .values({
        name: tag.name,
        normalizedName: normalizeName(tag.name),
        color: tag.color,
      })
      .returning({ id: tagsTable.id })
    if (!insertedTag?.id) {
      throw new Error(`Failed to create demo tag: ${tag.name}`)
    }
    tagIdByNormalizedName.set(normalizeName(tag.name), insertedTag.id)
  }

  const demoLocations: Array<InferInsertModel<typeof locationsTable> & { keyName: string }> = [
    {
      keyName: 'boise-id',
      name: "St. Luke's Boise Medical Center",
      normalizedName: "st. luke's boise medical center",
      city: 'Boise',
      region: 'ID',
      country: 'United States',
      latitude: '43.6150',
      longitude: '-116.2023',
    },
    {
      keyName: 'denver-co',
      name: 'Denver, Colorado',
      normalizedName: 'denver, colorado',
      city: 'Denver',
      region: 'CO',
      country: 'United States',
      latitude: '39.7392',
      longitude: '-104.9903',
    },
    {
      keyName: 'home-denver',
      name: 'Carter Family Home',
      normalizedName: 'carter family home',
      city: 'Denver',
      region: 'CO',
      country: 'United States',
      latitude: '39.7392',
      longitude: '-104.9847',
    },
    {
      keyName: 'yellowstone',
      name: 'Yellowstone National Park',
      normalizedName: 'yellowstone national park',
      region: 'WY',
      country: 'United States',
      latitude: '44.4280',
      longitude: '-110.5885',
    },
    {
      keyName: 'old-faithful',
      name: 'Old Faithful',
      normalizedName: 'old faithful',
      region: 'WY',
      country: 'United States',
      latitude: '44.4605',
      longitude: '-110.8281',
    },
    {
      keyName: 'grand-prismatic',
      name: 'Grand Prismatic Spring',
      normalizedName: 'grand prismatic spring',
      region: 'WY',
      country: 'United States',
      latitude: '44.5251',
      longitude: '-110.8382',
    },
    {
      keyName: 'lamar-valley',
      name: 'Lamar Valley',
      normalizedName: 'lamar valley',
      region: 'WY',
      country: 'United States',
      latitude: '44.8850',
      longitude: '-110.1828',
    },
    {
      keyName: 'coors-field',
      name: 'Coors Field',
      normalizedName: 'coors field',
      city: 'Denver',
      region: 'CO',
      country: 'United States',
      latitude: '39.7559',
      longitude: '-104.9942',
    },
    {
      keyName: 'red-rocks',
      name: 'Red Rocks Amphitheatre',
      normalizedName: 'red rocks amphitheatre',
      city: 'Morrison',
      region: 'CO',
      country: 'United States',
      latitude: '39.6654',
      longitude: '-105.2057',
    },
    {
      keyName: 'east-high',
      name: 'East High School',
      normalizedName: 'east high school',
      city: 'Denver',
      region: 'CO',
      country: 'United States',
      latitude: '39.7456',
      longitude: '-104.9596',
    },
    {
      keyName: 'denver-tech-center',
      name: 'Denver Tech Center',
      normalizedName: 'denver tech center',
      city: 'Denver',
      region: 'CO',
      country: 'United States',
      latitude: '39.6150',
      longitude: '-104.8790',
    },
    {
      keyName: 'breckenridge',
      name: 'Breckenridge Ski Resort',
      normalizedName: 'breckenridge ski resort',
      city: 'Breckenridge',
      region: 'CO',
      country: 'United States',
      latitude: '39.4817',
      longitude: '-106.0384',
    },
    {
      keyName: 'peak8',
      name: 'Peak 8 Base Area',
      normalizedName: 'peak 8 base area',
      city: 'Breckenridge',
      region: 'CO',
      country: 'United States',
      latitude: '39.4890',
      longitude: '-106.0670',
    },
    {
      keyName: 'frisco',
      name: 'Frisco Adventure Park',
      normalizedName: 'frisco adventure park',
      city: 'Frisco',
      region: 'CO',
      country: 'United States',
      latitude: '39.5744',
      longitude: '-106.0975',
    },
    {
      keyName: 'austin-tx',
      name: 'Zilker Botanical Garden',
      normalizedName: 'zilker botanical garden',
      city: 'Austin',
      region: 'TX',
      country: 'United States',
      latitude: '30.2711',
      longitude: '-97.7726',
    },
  ]

  const locationIdByKey = new Map<string, number>()
  for (const location of demoLocations) {
    const [insertedLocation] = await db
      .insert(locationsTable)
      .values({
        name: location.name,
        normalizedName: location.normalizedName,
        city: location.city,
        region: location.region,
        country: location.country,
        latitude: location.latitude,
        longitude: location.longitude,
      })
      .returning({ id: locationsTable.id })
    if (!insertedLocation?.id) {
      throw new Error(`Failed to create demo location: ${location.name}`)
    }
    locationIdByKey.set(location.keyName, insertedLocation.id)
  }

  const demoEvents: DemoEvent[] = [
    {
      key: 'wedding-2008',
      title: 'Daniel and Emily Get Married',
      description: 'A summer wedding with both sides of the family in attendance.',
      text: 'Small outdoor ceremony followed by a backyard reception in Boise.',
      date: new Date('2008-06-14T16:00:00-06:00'),
      locationKey: 'boise-id',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'robert', 'linda'],
      importance: 3,
    },
    {
      key: 'olivia-born-2009',
      title: 'Olivia Is Born',
      description: 'Welcomed our first child.',
      text: 'Healthy baby girl, 7 lbs 3 oz. Everyone instantly obsessed.',
      date: new Date('2009-09-22T07:35:00-06:00'),
      locationKey: 'boise-id',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'olivia'],
      importance: 3,
    },
    {
      key: 'noah-born-2011',
      title: 'Noah Is Born',
      description: 'Our second child arrived right before fall.',
      text: 'Olivia became a big sister and kept asking to hold him.',
      date: new Date('2011-08-29T10:10:00-06:00'),
      locationKey: 'boise-id',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'noah', 'olivia'],
      importance: 3,
    },
    {
      key: 'ava-born-2014',
      title: 'Ava Is Born',
      description: 'Third kid, and suddenly we felt outnumbered.',
      text: 'A calm birth and our first photo with all five of us.',
      date: new Date('2014-04-18T05:48:00-06:00'),
      locationKey: 'boise-id',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'ava', 'olivia', 'noah'],
      importance: 3,
    },
    {
      key: 'ethan-born-2017',
      title: 'Ethan Is Born',
      description: 'Our family of six is complete.',
      text: 'The kids took turns reading stories to him in the hospital room.',
      date: new Date('2017-01-09T14:20:00-07:00'),
      locationKey: 'boise-id',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'ethan', 'olivia', 'noah', 'ava'],
      importance: 3,
    },
    {
      key: 'move-denver-2018',
      title: 'Move to Denver',
      description: 'Packed up and moved for a fresh start and more mountain access.',
      text: 'Cross-state move, new schools, and first night in the new house.',
      date: new Date('2018-07-28T12:00:00-06:00'),
      locationKey: 'home-denver',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
      importance: 2,
    },
    {
      key: 'yellowstone-trip-2019',
      title: 'Yellowstone Summer Road Trip',
      description: 'Three-day family vacation through Yellowstone.',
      text: 'Trip album highlights:\nhttps://images.unsplash.com/photo-1501785888041-af3ef285b470\nhttps://images.unsplash.com/photo-1472396961693-142e6e269027',
      date: new Date('2019-07-12T08:30:00-06:00'),
      endDate: new Date('2019-07-14T19:30:00-06:00'),
      locationKey: 'yellowstone',
      tags: ['Vacation', 'Travel', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
      importance: 2,
    },
    {
      key: 'yellowstone-old-faithful-2019',
      parentKey: 'yellowstone-trip-2019',
      title: 'Day 1: Old Faithful and Junior Ranger Badges',
      description: 'Caught two eruptions and completed junior ranger booklets.',
      text: 'Noah timed the eruptions with a stopwatch; Olivia helped Ethan stamp his booklet.',
      date: new Date('2019-07-12T13:45:00-06:00'),
      locationKey: 'old-faithful',
      tags: ['Vacation', 'Family'],
      people: ['olivia', 'noah', 'ava', 'ethan', 'daniel', 'emily'],
    },
    {
      key: 'yellowstone-grand-prismatic-2019',
      parentKey: 'yellowstone-trip-2019',
      title: 'Day 2: Grand Prismatic Overlook Hike',
      description: 'Early start for cooler weather and better views.',
      text: 'Family photo on the overlook trail with steam rising in the background.',
      date: new Date('2019-07-13T09:15:00-06:00'),
      locationKey: 'grand-prismatic',
      tags: ['Vacation', 'Travel', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
    },
    {
      key: 'yellowstone-lamar-2019',
      parentKey: 'yellowstone-trip-2019',
      title: 'Day 3: Sunrise Wildlife Spotting in Lamar Valley',
      description: 'Bison herds, one distant wolf sighting, and lots of binocular passing.',
      text: 'Roadside breakfast burritos and coffee before heading home.',
      date: new Date('2019-07-14T06:50:00-06:00'),
      locationKey: 'lamar-valley',
      tags: ['Vacation', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
    },
    {
      key: 'olivia-birthday-2020',
      title: 'Olivia Turns 11',
      description: 'Backyard birthday with string lights and homemade cake.',
      text: 'Soccer teammates came over for games and a projector movie night.',
      date: new Date('2020-09-22T18:00:00-06:00'),
      locationKey: 'home-denver',
      tags: ['Birthday', 'Family'],
      people: ['olivia', 'daniel', 'emily', 'noah', 'ava', 'ethan'],
      importance: 2,
    },
    {
      key: 'noah-championship-2021',
      title: 'Noah Wins Little League Championship',
      description: 'Noah hit the go-ahead double in the fifth inning.',
      text: 'Team dogpile at second base and celebratory burgers after the game.',
      date: new Date('2021-06-19T16:30:00-06:00'),
      locationKey: 'coors-field',
      tags: ['Sports', 'Milestone', 'Family'],
      people: ['noah', 'daniel', 'emily', 'olivia', 'ava', 'ethan'],
      importance: 2,
    },
    {
      key: 'emily-promotion-2022',
      title: 'Emily Becomes Assistant Principal',
      description: 'A major career milestone after years in the classroom.',
      text: 'The family celebrated with dinner and a surprise cake from the kids.',
      date: new Date('2022-08-15T17:45:00-06:00'),
      locationKey: 'denver-tech-center',
      tags: ['Career', 'Milestone', 'Family'],
      people: ['emily', 'daniel', 'olivia', 'noah', 'ava', 'ethan'],
      importance: 2,
    },
    {
      key: 'olivia-middle-school-grad-2023',
      title: 'Olivia Graduates Middle School',
      description: 'Eighth-grade graduation and honors ribbon.',
      text: 'Family photos on campus and dinner at her favorite taco place.',
      date: new Date('2023-05-25T15:30:00-06:00'),
      locationKey: 'east-high',
      tags: ['School', 'Milestone', 'Family'],
      people: ['olivia', 'daniel', 'emily', 'noah', 'ava', 'ethan'],
      importance: 2,
    },
    {
      key: 'daniel-marathon-2023',
      title: 'Daniel Finishes His First Marathon',
      description: 'Crossed the line just under the 4-hour mark.',
      text: 'Kids made signs and waited near mile 25 to cheer him on.',
      date: new Date('2023-10-15T11:20:00-06:00'),
      locationKey: 'denver-co',
      tags: ['Milestone', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
      importance: 1,
    },
    {
      key: 'thanksgiving-2024',
      title: 'Thanksgiving at Home',
      description: 'First year hosting both grandparents in Denver.',
      text: 'Turkey, football, and a very competitive board game night.',
      date: new Date('2024-11-28T17:00:00-07:00'),
      locationKey: 'home-denver',
      tags: ['Holiday', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan', 'robert', 'linda'],
      importance: 1,
    },
    {
      key: 'ski-weekend-2025',
      title: 'Breckenridge Ski Weekend',
      description: 'Two-day winter weekend trip in the mountains.',
      text: 'Snowfall all weekend and everyone logged more runs than last year.',
      date: new Date('2025-01-18T08:00:00-07:00'),
      endDate: new Date('2025-01-19T17:30:00-07:00'),
      locationKey: 'breckenridge',
      tags: ['Vacation', 'Travel', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
      importance: 1,
    },
    {
      key: 'ski-lessons-2025',
      parentKey: 'ski-weekend-2025',
      title: 'Day 1: Ethan and Ava Take Ski Lessons',
      description: 'Bluebird morning and first successful chairlift rides.',
      text: 'Ethan linked his first full run without falling.',
      date: new Date('2025-01-18T11:10:00-07:00'),
      locationKey: 'peak8',
      tags: ['Vacation', 'Family', 'Sports'],
      people: ['ava', 'ethan', 'daniel', 'emily', 'olivia', 'noah'],
    },
    {
      key: 'snow-tubing-2025',
      parentKey: 'ski-weekend-2025',
      title: 'Day 2: Snow Tubing and Cocoa',
      description: 'All six of us raced down the tubing hill for an hour.',
      text: 'Finished with hot cocoa and a playlist in the lodge.',
      date: new Date('2025-01-19T14:00:00-07:00'),
      locationKey: 'frisco',
      tags: ['Vacation', 'Family'],
      people: ['daniel', 'emily', 'olivia', 'noah', 'ava', 'ethan'],
    },
    {
      key: 'olivia-high-school-grad-2025',
      title: 'Olivia Graduates High School',
      description: 'Caps, gowns, and a proud crowd in the stands.',
      text: 'Graduation day wrapped with a family trip to Austin for summer celebrations.',
      date: new Date('2025-05-30T19:00:00-06:00'),
      locationKey: 'austin-tx',
      tags: ['School', 'Milestone', 'Family', 'Achievement'],
      people: ['olivia', 'daniel', 'emily', 'noah', 'ava', 'ethan'],
      importance: 3,
    },
  ]

  const eventIdByKey = new Map<string, number>()

  for (const event of demoEvents) {
    const locationId = locationIdByKey.get(event.locationKey)
    if (!locationId) {
      throw new Error(`Missing demo location for key: ${event.locationKey}`)
    }

    let parentEventId: number | null = null
    if (event.parentKey) {
      parentEventId = eventIdByKey.get(event.parentKey) ?? null
      if (!parentEventId) {
        throw new Error(`Missing parent event "${event.parentKey}" for "${event.key}"`)
      }
    }

    const [insertedEvent] = await db
      .insert(eventsTable)
      .values({
        scopeId: scope.id,
        title: event.title,
        description: event.description,
        text: event.text,
        originalText: `demo:${event.key}`,
        date: event.date,
        endDate: event.endDate ?? null,
        parentEventId,
        locationId,
        importance: event.importance ?? 0,
      })
      .returning({ id: eventsTable.id })

    if (!insertedEvent?.id) {
      throw new Error(`Failed to create demo event: ${event.title}`)
    }
    eventIdByKey.set(event.key, insertedEvent.id)

    const personRows = event.people
      .map((personKey) => personIdByKey.get(personKey))
      .filter((personId): personId is number => Boolean(personId))
      .map((personId) => ({
        eventId: insertedEvent.id,
        personId,
        relevance: 1,
      }))
    if (personRows.length > 0) {
      await db.insert(eventPeopleTable).values(personRows)
    }

    const tagRows = event.tags
      .map((tagName) => tagIdByNormalizedName.get(normalizeName(tagName)))
      .filter((tagId): tagId is number => Boolean(tagId))
      .map((tagId) => ({
        eventId: insertedEvent.id,
        tagId,
      }))
    if (tagRows.length > 0) {
      await db.insert(eventTagsTable).values(tagRows)
    }
  }

  const [seededEventCheck] = await db
    .select({ id: eventsTable.id })
    .from(eventsTable)
    .where(eq(eventsTable.scopeId, scope.id))
    .limit(1)
  if (!seededEventCheck?.id) {
    throw new Error('Demo seed data verification failed')
  }
}
